import { useState } from 'react';
import { usePlayerStore } from '@/store/usePlayerStore';
import { useFavoritesStore } from '@/store/useFavoritesStore';
import { FlagIcon } from '@/components/common/FlagIcon';
import { Star, Share2, Lock, Unlock } from 'lucide-react';
import type { Channel } from '@/lib/channels';

interface PlayerInfoProps {
  channel: Channel;
}

export function PlayerInfo({ channel }: PlayerInfoProps) {
  const { error, retry, setError, playerLocked, togglePlayerLock } = usePlayerStore();
  const isFavorite = useFavoritesStore((s) => s.favoriteIds.includes(channel.id));
  const toggleFavorite = useFavoritesStore((s) => s.toggleFavorite);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  const handleCopy = async () => {
    try {
      // Share a deep link to this channel so others land on the app
      // with the channel already playing.
      const shareUrl = `${window.location.origin}/?channel=${encodeURIComponent(channel.id)}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      // ignore
    }
  };

  const handleToggleFavorite = () => {
    toggleFavorite(channel.id);
  };

  return (
    <div className="p-4 space-y-4">
      {/* Title + info */}
      <div>
        <h2 className="font-semibold text-base leading-tight mb-1" title={channel.name}>
          {channel.name}
        </h2>
        <div className="flex items-center gap-1.5 text-sm text-text-muted">
          <FlagIcon code={channel.countryCode} size="sm" />
          <span>{channel.country}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-3 gap-2 max-w-xs">
        <button
          onClick={handleToggleFavorite}
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          aria-pressed={isFavorite}
          className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg text-xs transition-colors ${
            isFavorite
              ? 'bg-error/10 hover:bg-error/20 text-error'
              : 'bg-bg-hover hover:bg-border text-text'
          }`}
        >
          <Star className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} />
          <span>{isFavorite ? 'Favorited' : 'Favorite'}</span>
        </button>
        <button
          onClick={handleCopy}
          className="flex flex-col items-center gap-1 py-2 px-1 bg-bg-hover hover:bg-border rounded-lg text-xs transition-colors"
        >
          <Share2 className="w-4 h-4" />
          <span>{copyState === 'copied' ? 'Copied!' : 'Copy URL'}</span>
        </button>
        <button
          onClick={togglePlayerLock}
          aria-label={playerLocked ? 'Unlock player' : 'Lock player'}
          aria-pressed={playerLocked}
          className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg text-xs transition-colors ${
            playerLocked
              ? 'bg-accent/10 text-accent'
              : 'bg-bg-hover hover:bg-border text-text'
          }`}
        >
          {playerLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
          <span>{playerLocked ? 'Locked' : 'Lock'}</span>
        </button>
      </div>

      {/* Error UI */}
      {error && (
        <div className="p-3 border border-error/20 bg-error/5 rounded-lg">
          <p className="text-sm text-error font-medium mb-1">Stream failed</p>
          <p className="text-xs text-text-muted mb-3">{error.message}</p>
          <div className="flex gap-2">
            <button
              onClick={retry}
              className="flex-1 py-1.5 px-2 bg-accent text-white rounded text-xs font-medium hover:bg-accent-hover transition-colors"
            >
              Retry
            </button>
          </div>
          <button
            onClick={() => setError(null)}
            className="mt-2 w-full py-1 px-2 text-xs text-text-muted hover:text-text transition-colors"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
